/**
 * @fileoverview Hook that provides primary navigation links configuration and responsive menu handling
 * @module features/primaryNavigation/routes/usePrimaryNavLinks
 */
import React, { useMemo } from 'react';
import { PrimaryNavLink } from 'basecamp';
import { useResponsiveMenu, useWindowSize } from '../../../utils/hooks';
import { translate } from '../../../utils/locale';
import { getFeature } from 'features/app/redux/features';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
	getCurrentSubjectUdfType,
	getCurrentSubjectUdfValue,
} from 'features/primaryNavigation/redux/selectors';

/**
 * Custom hook that manages primary navigation links and responsive menu behaviour
 * @returns {Object} Object containing large and small screen menu configurations
 * @returns {Array} largeScreenMenu - Array of menua items for large screen display
 * @returns {Array} smallScreenMenu - Array of menu items for small screen display
 */
const useSubjectsNavLinks = () => {
	//might need to check for an applied teaching set
	const { subject } = useParams<{ subject: string }>();
	const currentSubject = decodeURIComponent(subject);
	const currentSubjectUdfType = useSelector(getCurrentSubjectUdfType);
	const currentSubjectUdfValue = useSelector(getCurrentSubjectUdfValue);
	/**
	 * Check if the user has access to the subject area attainment page
	 */
	const canViewSubjectAreaAttainmentPage: boolean = useSelector(
		getFeature('subject_area_attainment_profile')
	);

	const internalLink =
		currentSubjectUdfType && currentSubjectUdfValue
			? `${encodeURIComponent(currentSubject)}/${currentSubjectUdfType}/${currentSubjectUdfValue}`
			: `${encodeURIComponent(currentSubject)}`;

	/**
	 * Configuration array for all primary navigation links
	 * Each object contains name, width, visibility, menu order and component details
	 * Memoized to prevent unnecessary re-renders
	 */
	const subjectsNavLinks = useMemo(
		() => [
			{
				name: 'subjectOverview',
				width: 110,
				isVisible: true,
				menuTransferOrder: 7,
				component: (
					<PrimaryNavLink
						setAs="secondary"
						data-test={`primary-nav-subject-overview-link`}
						withInternalUrl={`/subjects/${internalLink}/overview`}
						disabled={false}
					>
						{translate('primaryNavigation.connect.subjects.OVERVIEW')}
					</PrimaryNavLink>
				),
			},
			{
				name: 'subjectAttainment',
				width: 110,
				isVisible: canViewSubjectAreaAttainmentPage,
				menuTransferOrder: 6,
				component: (
					<PrimaryNavLink
						setAs="secondary"
						data-test={`primary-nav-subject-attainment-link`}
						withInternalUrl={`/subjects/${internalLink}/AttainmentProfile`}
						disabled={false}
					>
						{translate('primaryNavigation.connect.subjects.ATTAINMENT')}
					</PrimaryNavLink>
				),
			},
			{
				name: 'subjectStudents',
				width: 110,
				isVisible: true,
				menuTransferOrder: 5,
				component: (
					<PrimaryNavLink
						setAs="secondary"
						data-test={`primary-nav-subject-students-link`}
						withInternalUrl={`/subjects/${internalLink}/students`}
						disabled={false}
					>
						{translate('primaryNavigation.connect.subjects.STUDENTS')}
					</PrimaryNavLink>
				),
			},
			{
				name: 'subjectOutcomes',
				width: 110,
				isVisible: true,
				menuTransferOrder: 4,
				component: (
					<PrimaryNavLink
						setAs="secondary"
						data-test={`primary-nav-subject-outcomes-link`}
						withInternalUrl={`/subjects/${internalLink}/outcomes`}
						disabled={false}
					>
						{translate('primaryNavigation.connect.subjects.OUTCOMES')}
					</PrimaryNavLink>
				),
			},
			{
				name: 'subjectTrends',
				width: 110,
				isVisible: true,
				menuTransferOrder: 3,
				component: (
					<PrimaryNavLink
						setAs="secondary"
						data-test={`primary-nav-subject-trends-link`}
						withInternalUrl={`/subjects/${internalLink}/trends`}
						disabled={false}
					>
						{translate('primaryNavigation.connect.subjects.TRENDS')}
					</PrimaryNavLink>
				),
			},
			{
				name: 'subjectFineGrades',
				width: 110,
				isVisible: true,
				menuTransferOrder: 2,
				component: (
					<PrimaryNavLink
						setAs="secondary"
						data-test={`primary-nav-subject-fine-grades-link`}
						withInternalUrl={`/subjects/${internalLink}/fine-grades`}
						disabled={false}
					>
						{translate('primaryNavigation.connect.subjects.FINE_GRADES')}
					</PrimaryNavLink>
				),
			},
			{
				name: 'subjectSaved',
				width: 110,
				isVisible: true,
				menuTransferOrder: 1,
				component: (
					<PrimaryNavLink
						setAs="secondary"
						data-test={`primary-nav-subject-saved-link`}
						withInternalUrl={`/subjects/${internalLink}/saved`}
						disabled={false}
					>
						{translate('primaryNavigation.connect.subjects.SAVED')}
					</PrimaryNavLink>
				),
			},
		],
		[internalLink, canViewSubjectAreaAttainmentPage]
	);

	/**
	 * Get the current window width to determine responsive menu breakpoints
	 * @type {number} width - Current window width in pixels
	 */
	const { width } = useWindowSize();

	// Generate responsive menus based on screen size and dependencies
	// Uses subjectsNavLinks array to create large and small screen menu variants
	// Dependencies array ensures menu updates when relevant data changes
	const { largeScreenMenu, smallScreenMenu } = useResponsiveMenu(subjectsNavLinks, width, 0, [
		internalLink,
		canViewSubjectAreaAttainmentPage,
	]);

	return { largeScreenMenu, smallScreenMenu };
};

export default useSubjectsNavLinks;
